/**
 * Hook for managing graph node positions with caching
 *
 * Stateless by design: every call reads/writes localStorage directly
 * (synchronous, small blobs) rather than mirroring state in refs. That
 * avoids stale-state bugs when `regionId` changes between renders.
 */

import { useCallback } from 'react';
import type { Core } from 'cytoscape';
import {
  GraphPlacement,
  PositionMap,
  getPlacement as getPlacementUtil,
  savePositions as savePositionsUtil,
  loadStoredPinnedIds,
  clearPositions,
  extractPositionsFromCy,
} from '../utils/graphPositions';

interface UseGraphPositionsOptions {
  /** Region ID for position storage key */
  regionId: string | undefined;
  /** View ID for position storage key (allows multiple saved views per region) */
  viewId?: string;
}

interface UseGraphPositionsResult {
  /** Placement (cached + seeded positions, pinned set, unplaced set) for the given nodes */
  getPlacement: (nodeIds: string[]) => GraphPlacement;
  /** Snapshot all current node positions from cy; optionally mark one node as user-pinned */
  savePositions: (cy: Core, nodeIds: string[], pinNodeId?: string) => void;
  /** Clear all cached positions and pins for this region/view */
  clearCachedPositions: () => void;
}

export function useGraphPositions({
  regionId,
  viewId = 'default',
}: UseGraphPositionsOptions): UseGraphPositionsResult {
  const getPlacement = useCallback(
    (nodeIds: string[]): GraphPlacement => {
      if (!regionId || nodeIds.length === 0) {
        return { positions: {}, pinnedIds: [], unplacedIds: [] };
      }

      return getPlacementUtil(regionId, nodeIds, viewId);
    },
    [regionId, viewId]
  );

  const savePositions = useCallback(
    (cy: Core, nodeIds: string[], pinNodeId?: string) => {
      if (!regionId) return;

      // `extractPositionsFromCy` snapshots EVERY node currently in the cy
      // instance — which, with the endpoints overlay on, includes endpoint
      // satellite nodes. Those aren't part of `nodeIds` (topology device ids
      // only — they're re-derived every render from the parent device's
      // position, not persisted), so filter down to just the requested set
      // before writing, or satellite positions would leak into localStorage.
      const nodeIdSet = new Set(nodeIds);
      const allPositions = extractPositionsFromCy(cy);
      const positions: PositionMap = {};
      for (const id of Object.keys(allPositions)) {
        if (nodeIdSet.has(id)) positions[id] = allPositions[id];
      }

      const existingPinnedIds = loadStoredPinnedIds(regionId, viewId).filter((id) => nodeIds.includes(id));
      // Only ids in `nodeIds` may become pins — callers pass whatever node
      // was dragged, which can include endpoint satellites that must never
      // enter the persisted pin set.
      const pinnedIds =
        pinNodeId && nodeIdSet.has(pinNodeId) && !existingPinnedIds.includes(pinNodeId)
          ? [...existingPinnedIds, pinNodeId]
          : existingPinnedIds;

      savePositionsUtil(regionId, positions, pinnedIds, viewId);
    },
    [regionId, viewId]
  );

  const clearCachedPositions = useCallback(() => {
    if (!regionId) return;

    clearPositions(regionId, viewId);
  }, [regionId, viewId]);

  return {
    getPlacement,
    savePositions,
    clearCachedPositions,
  };
}
